/*
 * "Your referral code" cards + the on-device branded share card composer
 * (CANON V1.4 AMEND REFERRALS-PER-BUYER-CODE sections 7a, 11, 11b;
 * REFERRAL-BUILD-SPLIT-V1 §3). Pure presentational over a ReferralCodesView:
 *   null                      → NOTHING
 *   available:false           → one quiet "couldn't load" line
 *   available:true, no codes  → NOTHING (the /referrals page adds its own empty state)
 *   otherwise one card per active code, in API order (newest event first).
 *
 * COPY: copy.my_first_line / copy.my_body are settings' strings, shown
 * EXACTLY; nothing here re-derives offer wording. A line that would show a
 * "0%" figure is suppressed (belt and braces for the 7a hard rule).
 *
 * PRIVACY (canon section 11): the photo is read on the device
 * (URL.createObjectURL) and drawn into a canvas; the message lives in React
 * state only. Neither is ever put in a request — this file makes no request.
 * The composed PNG goes to the phone's share sheet (navigator.share) or to a
 * local download; never to Trumin.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ChangeEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { useLocale, type TranslateFn } from '../i18n/useLocale'
import type { ReferralCode, ReferralCodesView } from '../data/referralCodes'
import { resolveShareCard, type ShareCardSize } from '../lib/shareCardTemplate'
import {
  DEFAULT_CROP,
  ZOOM_MAX,
  ZOOM_MIN,
  clampPan,
  clampZoom,
  composeShareCardFile,
  drawShareCard,
  loadLocalImage,
  loadRemoteImage,
  type CardImage,
  type CardImages,
  type Crop,
} from '../lib/shareCardCompose'
import { shareReferral, type ShareNavigator, type ShareOutcome } from '../lib/referralShare'

const headingStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.1em', fontWeight: 400,
  textTransform: 'uppercase', color: 'var(--text-faint)', margin: '0 2px 8px',
}
const cardStyle: CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 15, padding: 15, marginBottom: 10,
}
const codeStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 800, letterSpacing: '.12em',
  background: 'var(--bg-frame)', border: '1px solid var(--hairline-14)', borderRadius: 10,
  padding: '6px 12px', color: 'var(--text)', userSelect: 'all', overflowWrap: 'anywhere',
}
const buttonStyle: CSSProperties = {
  border: '1px solid var(--hairline-14)', background: 'var(--surface)', color: 'var(--text-softbtn)',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  minHeight: 40, borderRadius: 10, padding: '0 14px', flex: 'none',
}
const primaryButtonStyle: CSSProperties = {
  ...buttonStyle, background: 'var(--accent)', color: 'var(--on-accent, #fff)', border: '1px solid var(--accent)',
}
const lineStyle: CSSProperties = { fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }
const hintStyle: CSSProperties = { fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.5 }
const labelStyle: CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }
const srOnly: CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden',
  clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
}

/** The "N friends used it" line. All referral copy is settings' (boardroom
 *  2026-10-28): copy.uses_one at N=1, copy.uses_other ({n} → the count)
 *  otherwise — shown at 0 too. The FE i18n strings are only the fallback for a
 *  settings that does not send them yet. */
export function usesLine(code: { uses: number; copy: { uses_one: string | null; uses_other: string | null } }, t: TranslateFn): string {
  const n = String(code.uses)
  if (code.uses === 1) return code.copy.uses_one ?? t('referral.uses.one')
  return code.copy.uses_other ? code.copy.uses_other.replace(/\{n\}/g, n) : t('referral.uses.other', { n })
}

/** 7a hard rule: never render a "0%" figure (10% is fine). */
const ZERO_PCT = /(^|[^0-9.,])0\s*%/
export const safeCopyLine = (s: string | null): string | null => (s && !ZERO_PCT.test(s) ? s : null)

function CopyButton({ value, label, ariaLabel, testId, t }: { value: string; label: string; ariaLabel?: string; testId: string; t: TranslateFn }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  const onClick = () => {
    let pending: Promise<void> | undefined
    try {
      pending = navigator.clipboard?.writeText(value)
    } catch {
      pending = undefined
    }
    if (!pending) return
    pending.then(
      () => {
        setCopied(true)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => setCopied(false), 2000)
      },
      () => { /* clipboard refused — the value stays visible and selectable */ },
    )
  }
  return (
    <>
      <button type="button" data-testid={testId} aria-label={copied ? t('referral.copied') : ariaLabel ?? label} onClick={onClick} style={buttonStyle}>
        {copied ? t('referral.copied') : label}
      </button>
      <span role="status" style={srOnly}>{copied ? t('referral.copied') : ''}</span>
    </>
  )
}

function IntentLinks({ code, t }: { code: ReferralCode; t: TranslateFn }) {
  const links: Array<[string, string | undefined]> = [
    ['WhatsApp', code.share_links.whatsapp],
    ['Facebook', code.share_links.facebook],
    ['X', code.share_links.x],
  ]
  return (
    <div data-testid="referral-intent-links" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
      <span style={hintStyle}>{t('referral.share.via')}</span>
      {links.map(([name, href]) =>
        href ? (
          <a key={name} data-testid={`referral-intent-${name.toLowerCase()}`} href={href} target="_blank" rel="noopener noreferrer" style={{ ...buttonStyle, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
            {name}
          </a>
        ) : null,
      )}
      <CopyButton value={code.share_page_url} label={t('referral.copyLink')} testId="referral-intent-copy-link" t={t} />
    </div>
  )
}

export interface ComposerProps {
  code: ReferralCode
  /** Injected for tests; defaults to the browser's navigator. */
  nav?: ShareNavigator
}

/** The branded share card composer (canvas, crop/zoom, message, share). */
export function ShareCardComposer({ code, nav }: ComposerProps) {
  const { t } = useLocale()
  const shareNav: ShareNavigator = nav ?? (typeof navigator !== 'undefined' ? (navigator as ShareNavigator) : {})
  const { template, plain } = useMemo(() => resolveShareCard(code), [code])
  const [message, setMessage] = useState(template.default_message)
  const [size, setSize] = useState<ShareCardSize>('square')
  const [crop, setCrop] = useState<Crop>(DEFAULT_CROP)
  const [photo, setPhoto] = useState<CardImage | null>(null)
  const [remote, setRemote] = useState<Pick<CardImages, 'hero' | 'logo'>>({ hero: null, logo: null })
  const [outcome, setOutcome] = useState<ShareOutcome | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState<{ url: string; name: string } | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const photoUrlRef = useRef<string | null>(null)
  const savedUrlRef = useRef<string | null>(null)
  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const gesture = useRef<{ crop: Crop; dist: number | null; x: number; y: number } | null>(null)

  const images: CardImages = useMemo(() => ({ photo, hero: remote.hero, logo: remote.logo }), [photo, remote])
  const L = template.layouts[size]

  // Template images: hero + logo (on-dark variant first, else the plain logo
  // on a light plate). crossOrigin="anonymous": a host without CORS for this
  // origin fails to load and the card is drawn without that image.
  useEffect(() => {
    if (plain) return
    let cancelled = false
    const logo = async () => {
      const dark = await loadRemoteImage(template.logo_on_dark_url)
      if (dark) return { ...dark, onDark: true }
      const light = await loadRemoteImage(template.logo_url)
      return light ? { ...light, onDark: false } : null
    }
    void Promise.all([loadRemoteImage(template.hero_image_url), logo()]).then(([hero, lg]) => {
      if (!cancelled) setRemote({ hero, logo: lg })
    })
    return () => { cancelled = true }
  }, [plain, template])

  // Revoke the device-local object URLs on unmount.
  useEffect(() => () => {
    if (photoUrlRef.current) URL.revokeObjectURL?.(photoUrlRef.current)
    if (savedUrlRef.current) URL.revokeObjectURL?.(savedUrlRef.current)
  }, [])

  // Live preview (drawn at full resolution, scaled by CSS).
  useEffect(() => {
    const cv = canvasRef.current
    if (!cv) return
    cv.width = L.w
    cv.height = L.h
    const ctx = cv.getContext('2d')
    if (!ctx) return
    drawShareCard(ctx, template, size, message, images, crop)
  }, [template, size, message, images, crop, L])

  // Any change makes an earlier "saved" image stale.
  useEffect(() => {
    setSaved(null)
  }, [template, size, message, images, crop])

  const onPhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return
    const url = URL.createObjectURL(file) // device-local; never uploaded
    void loadLocalImage(url).then((img) => {
      if (photoUrlRef.current) URL.revokeObjectURL?.(photoUrlRef.current)
      photoUrlRef.current = url
      setPhoto(img)
      setCrop(DEFAULT_CROP)
    })
  }

  const clearPhoto = () => {
    if (photoUrlRef.current) URL.revokeObjectURL?.(photoUrlRef.current)
    photoUrlRef.current = null
    setPhoto(null)
    setCrop(DEFAULT_CROP)
  }

  // Crop: one pointer drags, two pointers pinch; wheel and slider zoom.
  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    try { e.currentTarget.setPointerCapture?.(e.pointerId) } catch { /* not supported */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    gesture.current = { crop, dist: pinchDistance(), x: e.clientX, y: e.clientY }
  }
  const pinchDistance = () => {
    const p = [...pointers.current.values()]
    return p.length >= 2 ? Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) : null
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!pointers.current.has(e.pointerId) || !gesture.current) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const g = gesture.current
    const dist = pinchDistance()
    const startDist = g.dist
    if (dist !== null && startDist) {
      setCrop((c) => ({ ...c, zoom: clampZoom(g.crop.zoom * (dist / startDist)) }))
      return
    }
    const w = e.currentTarget.clientWidth || 1
    const h = e.currentTarget.clientHeight || 1
    setCrop((c) => ({ ...c, panX: clampPan(g.crop.panX + (e.clientX - g.x) / w), panY: clampPan(g.crop.panY + (e.clientY - g.y) / h) }))
  }
  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(e.pointerId)
    const rest = [...pointers.current.values()][0]
    gesture.current = rest ? { crop, dist: null, x: rest.x, y: rest.y } : null
  }
  // Wheel zoom needs a non-passive listener so the page does not scroll.
  useEffect(() => {
    const cv = canvasRef.current
    if (!cv) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      setCrop((c) => ({ ...c, zoom: clampZoom(c.zoom - Math.sign(e.deltaY) * 0.08) }))
    }
    cv.addEventListener('wheel', onWheel, { passive: false })
    return () => cv.removeEventListener('wheel', onWheel)
  }, [])

  const compose = useCallback(() => composeShareCardFile(template, size, message, images, crop), [template, size, message, images, crop])

  const onShare = async () => {
    setOutcome(null)
    if (typeof shareNav.share !== 'function') {
      setOutcome('unsupported')
      return
    }
    setBusy(true)
    let file: File | null = null
    try {
      file = (await compose()).file
    } catch {
      file = null // compose failed ⇒ text + link share
    }
    const res = await shareReferral(shareNav, { file, message, url: code.share_page_url })
    setBusy(false)
    setOutcome(res)
  }

  const onSave = async () => {
    if (typeof URL.createObjectURL !== 'function') return
    setBusy(true)
    const { file } = await compose().catch(() => ({ file: null }))
    setBusy(false)
    if (!file) return
    if (savedUrlRef.current) URL.revokeObjectURL?.(savedUrlRef.current)
    const url = URL.createObjectURL(file) // local blob: URL, never uploaded
    savedUrlRef.current = url
    setSaved({ url, name: file.name })
  }

  const zoomDisabled = !photo && !remote.hero
  const sizeButton = (s: ShareCardSize, label: string) => (
    <button
      type="button"
      data-testid={`referral-size-${s}`}
      aria-pressed={size === s}
      onClick={() => setSize(s)}
      style={{ ...buttonStyle, ...(size === s ? { border: '1px solid var(--accent)', color: 'var(--accent-bright, var(--text))' } : {}) }}
    >
      {label}
    </button>
  )

  return (
    <div data-testid="referral-composer" data-template={plain ? 'plain' : template.template_version} style={{ display: 'grid', gap: 12, marginTop: 14 }}>
      <div>
        <label style={labelStyle} htmlFor={`referral-photo-${code.code}`}>{t('referral.share.photo')}</label>
        <input id={`referral-photo-${code.code}`} data-testid="referral-photo" type="file" accept="image/*" onChange={onPhoto} />
        {photo && (
          <button type="button" data-testid="referral-photo-clear" onClick={clearPhoto} style={{ ...buttonStyle, marginTop: 8 }}>
            {t('referral.share.usePhotoless')}
          </button>
        )}
        <div style={{ ...hintStyle, marginTop: 6 }}>{t('referral.share.photoHint')}</div>
      </div>

      <div>
        <label style={labelStyle} htmlFor={`referral-message-${code.code}`}>{t('referral.share.message')}</label>
        <textarea
          id={`referral-message-${code.code}`}
          data-testid="referral-message"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--hairline-14)', borderRadius: 11, padding: 10, fontSize: 15, fontFamily: 'inherit', background: 'var(--bg-frame)', color: 'var(--text)' }}
        />
        <div style={hintStyle}>{t('referral.share.messageHint')}</div>
      </div>

      <div role="group" aria-label={t('referral.share.size')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {sizeButton('square', t('referral.share.square'))}
        {sizeButton('story', t('referral.share.story'))}
      </div>

      <figure style={{ margin: 0 }}>
        <canvas
          ref={canvasRef}
          data-testid="referral-preview"
          data-size={size}
          role="img"
          aria-label={t('referral.share.previewLabel', { w: String(L.w), h: String(L.h) })}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ width: '100%', maxWidth: size === 'square' ? 360 : 220, height: 'auto', display: 'block', borderRadius: 10, touchAction: 'none', background: template.colours.panel_bg }}
        />
        <figcaption style={{ ...hintStyle, marginTop: 6 }}>{t('referral.share.dragHint')}</figcaption>
      </figure>

      <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 10 }}>
        {t('referral.share.zoom')}
        <input
          type="range"
          data-testid="referral-zoom"
          min={ZOOM_MIN * 100}
          max={ZOOM_MAX * 100}
          value={Math.round(crop.zoom * 100)}
          disabled={zoomDisabled}
          onChange={(e) => setCrop((c) => ({ ...c, zoom: clampZoom(Number(e.target.value) / 100) }))}
        />
      </label>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" data-testid="referral-share" onClick={() => void onShare()} disabled={busy} style={primaryButtonStyle}>
          {busy ? t('referral.share.preparing') : t('referral.share.button')}
        </button>
        <button type="button" data-testid="referral-save" onClick={() => void onSave()} disabled={busy} style={buttonStyle}>
          {t('referral.share.save')}
        </button>
      </div>

      {outcome === 'failed' && <div data-testid="referral-share-failed" role="status" style={lineStyle}>{t('referral.share.failed')}</div>}
      {(outcome === 'unsupported' || outcome === 'failed') && <IntentLinks code={code} t={t} />}

      {saved && (
        <div data-testid="referral-saved" style={{ display: 'grid', gap: 6 }}>
          <a href={saved.url} download={saved.name} data-testid="referral-download" style={{ ...buttonStyle, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', justifySelf: 'start' }}>
            {t('referral.share.download')}
          </a>
          <img src={saved.url} alt={t('referral.share.previewLabel', { w: String(L.w), h: String(L.h) })} style={{ width: '100%', maxWidth: size === 'square' ? 360 : 220, borderRadius: 10 }} />
          <span style={hintStyle}>{t('referral.share.longPress')}</span>
        </div>
      )}
    </div>
  )
}

function ReferralCard({ code, highlight, nav }: { code: ReferralCode; highlight: boolean; nav?: ShareNavigator }) {
  const { t } = useLocale()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView?.({ block: 'start' })
  }, [highlight])
  const first = safeCopyLine(code.copy.my_first_line)
  const body = safeCopyLine(code.copy.my_body)
  return (
    <div
      ref={ref}
      id={`referral-${code.event_id}`}
      data-testid="referral-card"
      data-event-id={code.event_id}
      data-highlight={highlight ? 'true' : undefined}
      style={{ ...cardStyle, ...(highlight ? { border: '1px solid var(--accent)', boxShadow: '0 0 0 2px var(--accent)' } : {}) }}
    >
      <div data-testid="referral-event" style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{code.event_name}</div>
      <h3 style={{ fontSize: 17, fontWeight: 800, margin: '4px 0 10px' }}>{t('referral.cardTitle')}</h3>
      {first && <p data-testid="referral-first-line" style={{ ...lineStyle, color: 'var(--text)', fontWeight: 700, marginBottom: 8 }}>{first}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <code dir="ltr" data-testid="referral-code" style={codeStyle}>{code.code}</code>
        <CopyButton value={code.code} label={t('referral.copyCode')} ariaLabel={t('referral.copyCodeLabel', { code: code.code })} testId="referral-copy-code" t={t} />
      </div>
      {body && <p data-testid="referral-body" style={{ ...lineStyle, marginTop: 10 }}>{body}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
        <CopyButton value={code.share_page_url} label={t('referral.copyLink')} testId="referral-copy-link" t={t} />
        <button type="button" data-testid="referral-open-composer" aria-expanded={open} onClick={() => setOpen((v) => !v)} style={buttonStyle}>
          {open ? t('referral.share.close') : t('referral.share.open')}
        </button>
      </div>
      <div data-testid="referral-uses" style={{ ...hintStyle, marginTop: 10 }}>
        {usesLine(code, t)}
      </div>
      {open && <ShareCardComposer code={code} nav={nav} />}
    </div>
  )
}

export function ReferralCodesSection({ view, highlightEventId, nav }: { view: ReferralCodesView | null; highlightEventId?: string | null; nav?: ShareNavigator }) {
  const { t } = useLocale()
  if (!view) return null
  if (view.available && view.codes.length === 0) return null
  return (
    <section id="profile.referrals" data-testid="referral-section" aria-labelledby="referral-heading" style={{ marginBottom: 22 }}>
      <h2 id="referral-heading" style={headingStyle}>{t('referral.heading')}</h2>
      {!view.available ? (
        <div data-testid="referral-unavailable" style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, padding: '0 2px' }}>
          {t('referral.unavailable')}
        </div>
      ) : (
        view.codes.map((c) => <ReferralCard key={`${c.event_id}-${c.code}`} code={c} highlight={!!highlightEventId && c.event_id === highlightEventId} nav={nav} />)
      )}
    </section>
  )
}
